import * as t from "io-ts";
import * as reduxStore from "../../../redux-store";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createErrorReportingAsyncThunk } from "../../helpers";
import type { Dictionary } from "lodash";
import axios, { getData } from "../../../axios";
import keyBy from "lodash/keyBy";
import { flespiTokenT, TrackerDevice, trackerDevicesT } from "dora-contracts";
import { decode } from "io-ts-promise";
import { reverseGeoCode } from "../../../helpers/google-helpers";
import { DateTime } from "luxon";
import * as dependencies from "./dependencies";
import {
  selectFlespiDeviceIdFromDoraDeviceId,
  selectTelemetryPosition,
} from "./selectors";
import { AppThunkAction } from "../../../redux-store";
import { flespiTelemetryMessageReceivedForLiveViewRouteTruck } from "../live-view";

const prefix = "data/trackerDevices";

// export type FlespiMqttMessage = {
//   ident: string;
//   "position.latitude": number;
//   "position.longitude": number;
//   "server.timestamp": number;
//   timestamp: number;
//   place: string;
// };

export type TachoDriverStatus = {
  showStatus: boolean;
  continuousDrivingDuration?: number;
  cumulativeBreakDuration?: number;
  cumulativeDrivingDuration?: number;
  selectedActivityDuration?: number;
  timeState?: string;
  workState?: string;

  dailyBeforeRestDuration?: number;
  dailyDrivingDuration?: number;
  dailyLimitExceedNumber?: number; // show f 0?
  dailyMinimumRestDuration?: number;
  dailyRestEndTimestamp?: number;
  multiManningStatus?: boolean; // only show f true
  nextDrivingDuration?: number;
  nextRestDuration?: number;
  remainingBeforeRestDuration?: number;
  remainingCurrentDrivingDuration?: number;
  remainingCurrentRestDuration?: number;
  remainingReducedDailyRestPeriods?: number; // also show if 0
  remainingTenHourDrivingTimes?: number; // also show if 0
  remainingWeeklyDrivingDuration?: number;
  timeUntilNextDriving?: number;
  weeklyBeforeRestDuration?: number;
  weeklyDrivingDuration?: number;
  weeklyMinimumRestDuration?: number;
  weeklyRestEndTimestamp?: number; // datetime
  weeklySecondRestEndTimestamp?: number; // datetime
  workStateEnum?: number;
};

type TrackerDeviceTelemetry = {
  id: string;
  position: { lat: number; lon: number };
  ignitionStatus?: { value: boolean };
  movementStatus?: { value: boolean };
  tachoStats?: {
    vehicleMileage?: string;
    driver1: TachoDriverStatus;
    driver2: TachoDriverStatus;
  };
  location?: {
    updatedAt: DateTime;
    place: {
      placeName: string;
      address: string;
      postcode: string;
      city: string;
      country: string;
      coords: { lat: number; lon: number };
      googlePlaceId: string;
    };
  };
};

type State = {
  entities: Dictionary<TrackerDevice>;
  ids: string[];
  // devicesData: Dictionary<FlespiMqttMessage>;
  telemetry: Dictionary<TrackerDeviceTelemetry>;
  token: null | {
    token: string;
    expireAt: string;
  };
};

const initialState: State = {
  entities: {},
  ids: [],
  // devicesData: {},
  telemetry: {},
  token: null,
};

export const loadTrackerDevices = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (): Promise<TrackerDevice[]> => {
    return await axios
      .get("/api/tracker-devices")
      .then(getData)
      .then(decode(trackerDevicesT))
      .then((x) => x.data);
  }
);

export const loadDataFromTrackerDevice = createErrorReportingAsyncThunk(
  `${prefix}/loadDataFromTrackerDevice`,
  async (flespiMqttMessage: any) => {
    const reverseGeoCodingResult = await reverseGeoCode({
      lon: flespiMqttMessage["position.longitude"],
      lat: flespiMqttMessage["position.latitude"],
    });
    return {
      ...flespiMqttMessage,
      place: reverseGeoCodingResult.place,
    };
  }
);

export const lookupPositionForAllDevices =
  (): reduxStore.AppThunkAction => (dispatch, getState) => {
    const deviceIds = getState().data.trackerDevices.ids;
    return Promise.all(
      deviceIds.map((deviceId) => dispatch(lookupDevicePosition(deviceId)))
    );
  };

export const lookupDevicePosition =
  (doraDeviceId: string): AppThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    const position = selectTelemetryPosition(doraDeviceId)(state);

    if (!position) {
      return;
    }
    const flespiId = selectFlespiDeviceIdFromDoraDeviceId(doraDeviceId)(state);
    if (!flespiId) {
      return;
    }

    await dispatch(lookupDevicePositionWithTimestamp({ flespiId, position }));
  };

export const lookupDevicePositionWithTimestamp = createErrorReportingAsyncThunk(
  `${prefix}/lookupDevicePositionWithTimestamp`,
  async ({
    flespiId,
    position,
  }: {
    flespiId: number;
    position: { lat: number; lon: number };
  }) => {
    const timestamp = DateTime.utc();
    const place = await dependencies.reverseGeocode(position);
    return { timestamp, flespiId, place };
  }
);

export const loadFlespiToken = createErrorReportingAsyncThunk(
  `${prefix}/loadFlespiToken`,
  async () =>
    axios
      .get("/auth/flespi-token")
      .then(getData)
      .then(decode(t.union([flespiTokenT, t.null])))
);

const telemetryRegex =
  /^flespi\/state\/gw\/devices\/(?<deviceId>\d+)\/telemetry\/(?<key>.+)$/;

export const flespiTelemetryMessageReceived =
  ({ topic, message }: { topic: string; message: any }): AppThunkAction =>
  (dispatch, getState) => {
    const matches = telemetryRegex.exec(topic);
    if (matches) {
      const flespiDeviceId = matches.groups!["deviceId"];
      switch (matches.groups!["key"]) {
        case "position":
          const pos = {
            position: {
              lat: message.latitude,
              lon: message.longitude,
            },
            flespiDeviceId,
          };
          const state = getState();
          dispatch(slice.actions.updateTelemetryPosition(pos));

          const trackerDevice = Object.values(
            state.data.trackerDevices.entities
          ).find((d) => d.flespiId === Number(flespiDeviceId));
          const truck = Object.values(state.data.trailers.entities).find(
            (t) => t.trackerDeviceId === trackerDevice?.id
          );
          if (
            truck &&
            state.data.liveView.routeForLiveView?.vehicle.truckId === truck.id
          ) {
            dispatch(
              flespiTelemetryMessageReceivedForLiveViewRouteTruck({
                message: pos.position,
              })
            );
          }

          const deviceTelemetry =
            state.data.trackerDevices.telemetry[flespiDeviceId];
          // if (
          //   !deviceTelemetry ||
          //   (deviceTelemetry.location?.updatedAt &&
          //     Math.abs(deviceTelemetry.location?.updatedAt.diffNow().as("minutes")) > 2)
          // ) {
          if (!deviceTelemetry?.location) {
            dispatch(
              lookupDevicePositionWithTimestamp({
                flespiId: parseInt(pos.flespiDeviceId, 10),
                position: pos.position,
              })
            );
          }
          break;
        case "engine.ignition.status":
          dispatch(
            slice.actions.updateTelemetryIgnitionStatus({
              flespiDeviceId,
              value: message,
            })
          );
          break;
        case "movement.status":
          dispatch(
            slice.actions.updateTelemetryMovementStatus({
              flespiDeviceId,
              value: message,
            })
          );
          break;
        case "tacho.vehicle.mileage":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { vehicleMileage: message },
            })
          );
          break;
        case "tacho.driver.card.status.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1Status: message },
            })
          );
          break;
        case "tacho.driver.card.status.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2Status: message },
            })
          );
          break;
        case "tacho.driver.work.state.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WorkState: message },
            })
          );
          break;
        case "tacho.driver.work.state.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WorkState: message },
            })
          );
          break;
        case "tacho.driver.time.state.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1TimeState: message },
            })
          );
          break;
        case "tacho.driver.time.state.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2TimeState: message },
            })
          );
          break;
        case "tacho.driver.selected.activity.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1SelectedActivityDuration: message },
            })
          );
          break;
        case "tacho.driver.selected.activity.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2SelectedActivityDuration: message },
            })
          );
          break;
        case "tacho.driver.cumulative_driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1CumulativeDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.cumulative_driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2CumulativeDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.continuous_driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1ContinuousDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.continuous_driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2ContinuousDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.cumulative_break.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1CumulativeBreakDuration: message },
            })
          );
          break;
        case "tacho.driver.cumulative_break.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2CumulativeBreakDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.before.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1DailyBeforeRestDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.before.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2DailyBeforeRestDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1DailyDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2DailyDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.limit.exceed.number.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1DailyLimitExceedNumber: message },
            })
          );
          break;
        case "tacho.driver.daily.limit.exceed.number.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2DailyLimitExceedNumber: message },
            })
          );
          break;
        case "tacho.driver.daily.minimum.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1DailyMinimumRestDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.minimum.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2DailyMinimumRestDuration: message },
            })
          );
          break;
        case "tacho.driver.daily.rest.end.timestamp.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1DailyRestEndTimestamp: message },
            })
          );
          break;
        case "tacho.driver.daily.rest.end.timestamp.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2DailyRestEndTimestamp: message },
            })
          );
          break;
        case "tacho.driver.multi.manning.status.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1MultiManningStatus: message },
            })
          );
          break;
        case "tacho.driver.multi.manning.status.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2MultiManningStatus: message },
            })
          );
          break;
        case "tacho.driver.next.driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1NextDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.next.driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2NextDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.next.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1NextRestDuration: message },
            })
          );
          break;
        case "tacho.driver.next.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2NextRestDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.before.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1RemainingBeforeRestDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.before.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2RemainingBeforeRestDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.current.driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1RemainingCurrentDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.current.driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2RemainingCurrentDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.current.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1RemainingCurrentRestDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.current.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2RemainingCurrentRestDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.reduced.daily.rest.periods.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1RemainingReducedDailyRestPeriods: message },
            })
          );
          break;
        case "tacho.driver.remaining.reduced.daily.rest.periods.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2RemainingReducedDailyRestPeriods: message },
            })
          );
          break;
        case "tacho.driver.remaining.ten.hour.driving.times.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1RemainingTenHourDrivingTimes: message },
            })
          );
          break;
        case "tacho.driver.remaining.ten.hour.driving.times.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2RemainingTenHourDrivingTimes: message },
            })
          );
          break;
        case "tacho.driver.remaining.weekly.driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1RemainingWeeklyDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.remaining.weekly.driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2RemainingWeeklyDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.time.until.next.driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1TimeUntilNextDriving: message },
            })
          );
          break;
        case "tacho.driver.time.until.next.driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2TimeUntilNextDriving: message },
            })
          );
          break;
        case "tacho.driver.weekly.before.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WeeklyBeforeRestDuration: message },
            })
          );
          break;
        case "tacho.driver.weekly.before.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WeeklyBeforeRestDuration: message },
            })
          );
          break;
        case "tacho.driver.weekly.driving.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WeeklyDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.weekly.driving.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WeeklyDrivingDuration: message },
            })
          );
          break;
        case "tacho.driver.weekly.minimum.rest.duration.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WeeklyMinimumRestDuration: message },
            })
          );
          break;
        case "tacho.driver.weekly.minimum.rest.duration.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WeeklyMinimumRestDuration: message },
            })
          );
          break;
        case "tacho.driver.weekly.rest.end.timestamp.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WeeklyRestEndTimestamp: message },
            })
          );
          break;
        case "tacho.driver.weekly.rest.end.timestamp.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WeeklyRestEndTimestamp: message },
            })
          );
          break;
        case "tacho.driver.weekly.second.rest.end.timestamp.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WeeklySecondRestEndTimestamp: message },
            })
          );
          break;
        case "tacho.driver.weekly.second.rest.end.timestamp.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WeeklySecondRestEndTimestamp: message },
            })
          );
          break;
        case "tacho.driver.work.state.enum.1":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver1WorkStateEnum: message },
            })
          );
          break;
        case "tacho.driver.work.state.enum.2":
          dispatch(
            slice.actions.updateTelemetryTachoAttributesStatus({
              flespiDeviceId,
              value: { driver2WorkStateEnum: message },
            })
          );
          break;
      }
    }
  };

type UpdateTelemetryPositionPayload = {
  position: { lat: number; lon: number };
  flespiDeviceId: string;
};

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    updateTelemetryPosition(
      state,
      action: PayloadAction<UpdateTelemetryPositionPayload>
    ) {
      const { flespiDeviceId, position } = action.payload;
      state.telemetry[flespiDeviceId] = {
        ...state.telemetry[flespiDeviceId],
        id: flespiDeviceId,
        position,
      };
    },
    updateTelemetryTachoAttributesStatus(
      state,
      action: PayloadAction<{
        flespiDeviceId: string;
        value: {
          vehicleMileage?: string;
          driver1Status?: boolean;
          driver1ContinuousDrivingDuration?: number;
          driver1CumulativeBreakDuration?: number;
          driver1CumulativeDrivingDuration?: number;
          driver1SelectedActivityDuration?: number;
          driver1TimeState?: string;
          driver1WorkState?: string;
          driver1DailyBeforeRestDuration?: number;
          driver1DailyDrivingDuration?: number;
          driver1DailyLimitExceedNumber?: number; // show f 0?
          driver1DailyMinimumRestDuration?: number;
          driver1DailyRestEndTimestamp?: number;
          driver1MultiManningStatus?: boolean; // only show f true
          driver1NextDrivingDuration?: number;
          driver1NextRestDuration?: number;
          driver1RemainingBeforeRestDuration?: number;
          driver1RemainingCurrentDrivingDuration?: number;
          driver1RemainingCurrentRestDuration?: number;
          driver1RemainingReducedDailyRestPeriods?: number; // also show if 0
          driver1RemainingTenHourDrivingTimes?: number; // also show if 0
          driver1RemainingWeeklyDrivingDuration?: number;
          driver1TimeUntilNextDriving?: number;
          driver1WeeklyBeforeRestDuration?: number;
          driver1WeeklyDrivingDuration?: number;
          driver1WeeklyMinimumRestDuration?: number;
          driver1WeeklyRestEndTimestamp?: number; // datetime
          driver1WeeklySecondRestEndTimestamp?: number; // datetime
          driver1WorkStateEnum?: number;

          driver2Status?: boolean;
          driver2ContinuousDrivingDuration?: number;
          driver2CumulativeBreakDuration?: number;
          driver2CumulativeDrivingDuration?: number;
          driver2SelectedActivityDuration?: number;
          driver2TimeState?: string;
          driver2WorkState?: string;
          driver2DailyBeforeRestDuration?: number;
          driver2DailyDrivingDuration?: number;
          driver2DailyLimitExceedNumber?: number; // show f 0?
          driver2DailyMinimumRestDuration?: number;
          driver2DailyRestEndTimestamp?: number;
          driver2MultiManningStatus?: boolean; // only show f true
          driver2NextDrivingDuration?: number;
          driver2NextRestDuration?: number;
          driver2RemainingBeforeRestDuration?: number;
          driver2RemainingCurrentDrivingDuration?: number;
          driver2RemainingCurrentRestDuration?: number;
          driver2RemainingReducedDailyRestPeriods?: number; // also show if 0
          driver2RemainingTenHourDrivingTimes?: number; // also show if 0
          driver2RemainingWeeklyDrivingDuration?: number;
          driver2TimeUntilNextDriving?: number;
          driver2WeeklyBeforeRestDuration?: number;
          driver2WeeklyDrivingDuration?: number;
          driver2WeeklyMinimumRestDuration?: number;
          driver2WeeklyRestEndTimestamp?: number; // datetime
          driver2WeeklySecondRestEndTimestamp?: number; // datetime
          driver2WorkStateEnum?: number;
        };
      }>
    ) {
      const { flespiDeviceId, value } = action.payload;
      if (!state.telemetry[flespiDeviceId]?.tachoStats) {
        state.telemetry[flespiDeviceId] = {
          ...state.telemetry[flespiDeviceId],
          tachoStats: {
            driver1: { showStatus: false },
            driver2: { showStatus: false },
          },
        };
      }
      const tachoStats = state.telemetry[flespiDeviceId].tachoStats!;

      if (typeof value.driver1Status === "boolean") {
        tachoStats.driver1.showStatus = value.driver1Status;
      }
      if (typeof value.driver2Status === "boolean") {
        tachoStats.driver2.showStatus = value.driver2Status;
      }
      if (value.driver1WorkState) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          workState: value.driver1WorkState,
        };
      }
      if (value.driver1TimeState) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          timeState: value.driver1TimeState,
        };
      }
      if (value.driver1SelectedActivityDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          selectedActivityDuration: value.driver1SelectedActivityDuration,
        };
      }
      if (value.driver1CumulativeDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          cumulativeDrivingDuration: value.driver1CumulativeDrivingDuration,
        };
      }
      if (value.driver1ContinuousDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          continuousDrivingDuration: value.driver1ContinuousDrivingDuration,
        };
      }
      if (value.driver1CumulativeBreakDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          cumulativeBreakDuration: value.driver1CumulativeBreakDuration,
        };
      }
      if (value.driver2WorkState) {
        tachoStats.driver2 = {
          ...tachoStats.driver2,
          workState: value.driver2WorkState,
        };
      }
      if (value.driver2TimeState) {
        tachoStats.driver2 = {
          ...tachoStats.driver2,
          timeState: value.driver2TimeState,
        };
      }
      if (value.driver2SelectedActivityDuration) {
        tachoStats.driver2 = {
          ...tachoStats.driver2,
          selectedActivityDuration: value.driver2SelectedActivityDuration,
        };
      }
      if (value.driver2CumulativeDrivingDuration) {
        tachoStats.driver2 = {
          ...tachoStats.driver2,
          cumulativeDrivingDuration: value.driver2CumulativeDrivingDuration,
        };
      }
      if (value.driver2ContinuousDrivingDuration) {
        tachoStats.driver2 = {
          ...tachoStats.driver2,
          continuousDrivingDuration: value.driver2ContinuousDrivingDuration,
        };
      }
      if (value.driver2CumulativeBreakDuration) {
        tachoStats.driver2 = {
          ...tachoStats.driver2,
          cumulativeBreakDuration: value.driver2CumulativeBreakDuration,
        };
      }
      if (value.vehicleMileage) {
        tachoStats.vehicleMileage = value.vehicleMileage;
      }
      if (value.driver1DailyBeforeRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          dailyBeforeRestDuration: value.driver1DailyBeforeRestDuration,
        };
      }
      if (value.driver1DailyDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          dailyDrivingDuration: value.driver1DailyDrivingDuration,
        };
      }
      tachoStats.driver1 = {
        ...tachoStats.driver1,
        dailyLimitExceedNumber: value.driver1DailyLimitExceedNumber,
      };
      if (value.driver1DailyMinimumRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          dailyMinimumRestDuration: value.driver1DailyMinimumRestDuration,
        };
      }
      if (value.driver1DailyRestEndTimestamp) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          dailyRestEndTimestamp: value.driver1DailyRestEndTimestamp,
        };
      }
      if (value.driver1MultiManningStatus) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          multiManningStatus: value.driver1MultiManningStatus,
        };
      }
      if (value.driver1NextDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          nextDrivingDuration: value.driver1NextDrivingDuration,
        };
      }
      if (value.driver1NextRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          nextRestDuration: value.driver1NextRestDuration,
        };
      }
      if (value.driver1RemainingBeforeRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          remainingBeforeRestDuration: value.driver1RemainingBeforeRestDuration,
        };
      }
      if (value.driver1RemainingCurrentDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          remainingCurrentDrivingDuration:
            value.driver1RemainingCurrentDrivingDuration,
        };
      }
      if (value.driver1RemainingCurrentRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          remainingCurrentRestDuration:
            value.driver1RemainingCurrentRestDuration,
        };
      }
      if (value.driver1RemainingReducedDailyRestPeriods) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          remainingReducedDailyRestPeriods:
            value.driver1RemainingReducedDailyRestPeriods,
        };
      }
      if (value.driver1RemainingTenHourDrivingTimes) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          remainingTenHourDrivingTimes:
            value.driver1RemainingTenHourDrivingTimes,
        };
      }
      if (value.driver1RemainingWeeklyDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          remainingWeeklyDrivingDuration:
            value.driver1RemainingWeeklyDrivingDuration,
        };
      }
      if (value.driver1TimeUntilNextDriving) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          timeUntilNextDriving: value.driver1TimeUntilNextDriving,
        };
      }
      if (value.driver1WeeklyBeforeRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          weeklyBeforeRestDuration: value.driver1WeeklyBeforeRestDuration,
        };
      }
      if (value.driver1WeeklyDrivingDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          weeklyDrivingDuration: value.driver1WeeklyDrivingDuration,
        };
      }
      if (value.driver1WeeklyMinimumRestDuration) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          weeklyMinimumRestDuration: value.driver1WeeklyMinimumRestDuration,
        };
      }
      if (value.driver1WeeklyRestEndTimestamp) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          weeklyRestEndTimestamp: value.driver1WeeklyRestEndTimestamp,
        };
      }
      if (value.driver1WeeklySecondRestEndTimestamp) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          weeklySecondRestEndTimestamp:
            value.driver1WeeklySecondRestEndTimestamp,
        };
      }
      if (value.driver1WorkStateEnum) {
        tachoStats.driver1 = {
          ...tachoStats.driver1,
          workStateEnum: value.driver1WorkStateEnum,
        };
      }


      state.telemetry[flespiDeviceId] = {
        ...state.telemetry[flespiDeviceId],
        tachoStats,
      };
    },
    updateTelemetryIgnitionStatus(
      state,
      action: PayloadAction<{ flespiDeviceId: string; value: boolean }>
    ) {
      const { flespiDeviceId, value } = action.payload;
      state.telemetry[flespiDeviceId] = {
        ...state.telemetry[flespiDeviceId],
        ignitionStatus: { value },
      };
    },
    updateTelemetryMovementStatus(
      state,
      action: PayloadAction<{ flespiDeviceId: string; value: boolean }>
    ) {
      const { flespiDeviceId, value } = action.payload;
      state.telemetry[flespiDeviceId] = {
        ...state.telemetry[flespiDeviceId],
        movementStatus: { value },
      };
    },
    flespiTelemetryMessageReceived(
      state,
      action: PayloadAction<{ topic: string; message: any }>
    ) {
      const { topic, message } = action.payload;
      const matches = telemetryRegex.exec(topic);
      if (matches) {
        const deviceId = matches.groups!["deviceId"];
        switch (matches.groups!["key"]) {
          case "position": {
            state.telemetry[deviceId] = {
              ...state.telemetry[deviceId],
              id: deviceId,
              position: { lat: message.latitude, lon: message.longitude },
            };
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadTrackerDevices.fulfilled, (state, action) => {
        const trackerDevices = action.payload;
        state.entities = keyBy(trackerDevices, "id");
        state.ids = trackerDevices.map((x) => x.id);
      })
      .addCase(loadFlespiToken.fulfilled, (state, action) => {
        state.token = action.payload;
      })
      // .addCase(loadDataFromTrackerDevice.fulfilled, (state, action) => {
      //   const message = action.payload;
      //   const deviceFlespiId: number = message["device.id"];
      //
      //   state.devicesData[deviceFlespiId] = message;
      // })
      .addCase(lookupDevicePositionWithTimestamp.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }
        const { flespiId, timestamp, place } = action.payload;
        const telemetry = state.telemetry[flespiId];
        if (telemetry) {
          if (
            telemetry.location?.updatedAt &&
            telemetry.location.updatedAt > timestamp
          ) {
            return;
          }
          // Always true, but nevermind.
          telemetry.location = {
            updatedAt: timestamp,
            place,
          };
        }
      });
  },
});

export default slice.reducer;
